import React from 'react';
import PropTypes from 'prop-types';

export default class Spotlight extends React.Component {
    constructor(props) {
        super(props);

        const isServerSide = typeof window === 'undefined';
        // If the user has server rendering, we assume they have a 4k screen (better aim high than low just in case).
        this.state = {
            windowWidth: isServerSide ? 3840 : window.innerWidth,
            windowHeight: isServerSide ? 2160 : window.innerHeight,
        };

        this.resizeThrottler = this.resizeThrottler.bind(this);
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        return {
            width: nextProps.width,
            height: nextProps.height,
            top: nextProps.top,
            left: nextProps.left,
        };
    }

    componentDidMount() {
        if (this.props.responsive)
            window.addEventListener('resize', this.resizeThrottler);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.resizeThrottler);
    }

    // https://developer.mozilla.org/en-US/docs/Web/Events/resize#setTimeout
    resizeThrottler() {
        // ignore resize events as long as an actualResizeHandler execution is in the queue
        if (!this.resizeTimeout) {
            this.resizeTimeout = setTimeout(() => {
                this.resizeTimeout = null;
                this.recalc();
            }, 66); // The actualResizeHandler will execute at a rate of 15fps
        }
    }

    recalc() {
        this.setState({
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
        });
    }

    getBorderSize() {
        // We find the diagonal of the screen and add the circle's radius to ensure
        // that the border covers the screen when the circle is just off screen.
        const diagonal = Math.ceil(
            Math.sqrt(
                Math.pow(this.state.windowWidth, 2) +
                Math.pow(this.state.windowHeight, 2)
            )
        );
        return diagonal + this.props.radius;
    }

    getX() {
        let pos = this.props.usePercentage
            ? this.state.width * this.props.x / 100
            : this.props.x;
        pos += this.props.left
        return `${pos}px`;
    }

    getY() {
        let pos = this.props.usePercentage
            ? this.state.height * this.props.y / 100
            : this.props.y;
        pos += this.props.top
        return `${pos}px`;
    }

    getInnerStyles() {
        const {animSpeed, radius, borderWidth, color, innerStyles} = this.props;
        const diameter = radius * 2 + borderWidth * 2;

        return {
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: diameter,
            height: diameter,
            transform: 'translate(-50%, -50%)',
            border: `${this.getBorderSize()}px solid ${color}`,
            opacity: 0.9,
            borderRadius: '50%',
            boxSizing: 'content-box',
            transitionDuration: `${animSpeed}ms`,
            transitionProperty: 'all',
            ...innerStyles,
        };
    }

    getOuterStyles() {
        const {
            animSpeed,
            borderWidth,
            borderColor,
            radius,
            outerStyles,
        } = this.props;
        const diameter = radius * 2;

        return {
            position: 'fixed',
            left: -radius - borderWidth,
            top: -radius - borderWidth,
            width: diameter,
            height: diameter,
            borderRadius: '50%',
            zIndex: 999,
            boxSizing: 'content-box',
            border: `${borderWidth}px solid ${borderColor}`,
            transform: `translate(${this.getX()}, ${this.getY()})`,
            transitionDuration: `${animSpeed}ms`,
            transitionProperty: 'all',
            ...outerStyles,
        };
    }

    render() {
        return (
            <div
                style={this.getOuterStyles()}
                className={this.props.outerClass || ''}
            >
                <div
                    style={this.getInnerStyles()}
                    className={this.props.innerClass || ''}
                >
                    {this.props.children}
                </div>
            </div>
        );
    }
}

Spotlight.propTypes = {
    color: PropTypes.string,
    x: PropTypes.number,
    y: PropTypes.number,
    radius: PropTypes.number,
    responsive: PropTypes.bool,
    outerClass: PropTypes.string,
    innerClass: PropTypes.string,
    outerStyles: PropTypes.object,
    innerStyles: PropTypes.object,
    borderWidth: PropTypes.number,
    borderColor: PropTypes.string,
    animSpeed: PropTypes.number,
    usePercentage: PropTypes.bool,
    width: PropTypes.number,
    height: PropTypes.number,
    top: PropTypes.number,
    left: PropTypes.number,
};

Spotlight.defaultProps = {
    color: 'black',
    x: 50,
    y: 50,
    radius: 100,
    responsive: true,
    usePercentage: false,
    borderWidth: 0,
    borderColor: 'white',
    animSpeed: 500,
    outerStyles: {},
    innerStyles: {},
};
